import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Grid from "@mui/material/Grid";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { getNavigationReport } from "../../services/Dashboard";
import { getColumnsData } from "./NavigationReportColumns";
import { useQuery } from "react-query";
import { isValidDate } from "../../services/Utils";
import SearchIcon from "@mui/icons-material/Search";
import getDataGridToolbarProps from "../common/CustomToolbar";

const FILTER_TYPES = {
  MONTH: "month",
  RANGE: "range",
};

const NavigationReport = (props) => {
  const CustomToolbar = getDataGridToolbarProps("Navigation Report", true);
  const columns = getColumnsData();
  const [filterType, setFilterType] = useState(FILTER_TYPES.MONTH);
  const [picker, setPicker] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1, 0, 0, 0, 0),
  );

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [pickerInputErrorText, setPickerInputErrorText] = useState(null);
  const [searchParams, setSearchParams] = useState({
    type: FILTER_TYPES.MONTH,
    params: { month: picker.getMonth() + 1, year: picker.getFullYear() },
  });

  const [rows, setRows] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [fetchData, setFetchData] = useState(true);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const { isLoading: logsLoading, isFetching } = useQuery(
    ["navigation_report", searchParams],
    getNavigationReport,
    {
      enabled: fetchData,
      onSuccess: ({ data }) => {
        setRows(data);
        setPage(0);
      },
      onSettled: () => {
        setFetchData(false);
      },
      refetchOnWindowFocus: false,
    },
  );

  const handleMonthChange = (val = null) => {
    if (!isValidDate(new Date(val))) {
      setPickerInputErrorText("Invalid Month!");
      return;
    }
    setPickerInputErrorText(null);
    setPicker(val);
  };

  const processSearch = () => {
    if (filterType === FILTER_TYPES.MONTH) {
      if (!isValidDate(new Date(picker))) return;
      setSearchParams({
        type: FILTER_TYPES.MONTH,
        params: { month: picker.getMonth() + 1, year: picker.getFullYear() },
      });
    } else {
      if (!isValidDate(new Date(fromDate)) || !isValidDate(new Date(toDate)))
        return;
      if (fromDate > toDate) {
        setPickerInputErrorText("From Date must be before To Date");
        return;
      }
      setPickerInputErrorText(null);
      setSearchParams({
        type: FILTER_TYPES.RANGE,
        params: {
          fromDate: fromDate.toISOString(),
          toDate: toDate.toISOString(),
        },
      });
    }
    setFetchData(true);
  };

  const totalPages = Math.ceil((rows?.length || 0) / pageSize) || 1;

  const commonProps = { refresh, setRefresh };

  return (
    <div className="fragmentContainer">
      <Grid container spacing={2}>
        <Grid item xs={12} md={12} lg={12}>
          <Card sx={{ minHeight: "96px" }}>
            <CardContent className="tile-cards">
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <div
                  style={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "16px",
                  }}
                >
                  <FormControl component="fieldset">
                    <RadioGroup
                      row
                      value={filterType}
                      onChange={(e) => {
                        setFilterType(e.target.value);
                        setPickerInputErrorText(null);
                      }}
                    >
                      <FormControlLabel
                        value={FILTER_TYPES.MONTH}
                        control={<Radio size="small" />}
                        label="Month"
                      />
                      <FormControlLabel
                        value={FILTER_TYPES.RANGE}
                        control={<Radio size="small" />}
                        label="Date Range"
                      />
                    </RadioGroup>
                  </FormControl>

                  {filterType === FILTER_TYPES.MONTH && (
                    <FormControl variant="standard" sx={{ minWidth: 120 }}>
                      <DatePicker
                        disableFuture
                        label="Month"
                        views={["month", "year"]}
                        value={picker}
                        timezone="UTC"
                        onChange={(val) => handleMonthChange(val)}
                        renderInput={(params) => (
                          <TextField
                            size="small"
                            {...params}
                            id="standard-basic"
                            variant="standard"
                            error={!!pickerInputErrorText}
                            onKeyDown={(e) => e.preventDefault()}
                            helperText={pickerInputErrorText}
                          />
                        )}
                        maxDate={new Date()}
                        minDate={new Date("01-01-2023")}
                        required
                      />
                    </FormControl>
                  )}

                  {filterType === FILTER_TYPES.RANGE && (
                    <>
                      <FormControl variant="standard" sx={{ minWidth: 150 }}>
                        <DatePicker
                          disableFuture
                          label="From Date"
                          views={["year", "month", "day"]}
                          value={fromDate}
                          timezone="UTC"
                          onChange={(val) => {
                            if (!isValidDate(new Date(val))) {
                              setPickerInputErrorText("Invalid From Date!");
                              return;
                            }
                            setPickerInputErrorText(null);
                            setFromDate(val);
                          }}
                          renderInput={(params) => (
                            <TextField
                              size="small"
                              {...params}
                              id="standard-basic-from"
                              variant="standard"
                              onKeyDown={(e) => e.preventDefault()}
                            />
                          )}
                          maxDate={toDate || new Date()}
                          minDate={new Date("01-01-2023")}
                          required
                        />
                      </FormControl>

                      <FormControl variant="standard" sx={{ minWidth: 150 }}>
                        <DatePicker
                          disableFuture
                          label="To Date"
                          views={["year", "month", "day"]}
                          value={toDate}
                          timezone="UTC"
                          onChange={(val) => {
                            if (!isValidDate(new Date(val))) {
                              setPickerInputErrorText("Invalid To Date!");
                              return;
                            }
                            setPickerInputErrorText(null);
                            setToDate(val);
                          }}
                          renderInput={(params) => (
                            <TextField
                              size="small"
                              {...params}
                              id="standard-basic-to"
                              variant="standard"
                              onKeyDown={(e) => e.preventDefault()}
                            />
                          )}
                          maxDate={new Date()}
                          minDate={fromDate || new Date("01-01-2023")}
                          required
                        />
                      </FormControl>
                    </>
                  )}

                  <Button
                    disabled={
                      !!pickerInputErrorText ||
                      (filterType === FILTER_TYPES.RANGE &&
                        (!fromDate || !toDate))
                    }
                    variant="outlined"
                    startIcon={<SearchIcon />}
                    onClick={() => processSearch()}
                  >
                    Search
                  </Button>

                  {pickerInputErrorText &&
                    filterType === FILTER_TYPES.RANGE && (
                      <div
                        style={{
                          color: "#d32f2f",
                          fontSize: "0.75rem",
                          width: "100%",
                        }}
                      >
                        {pickerInputErrorText}
                      </div>
                    )}
                </div>
              </LocalizationProvider>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={12} lg={12}>
          <Card>
            <CardContent sx={{ padding: 0, ":last-child": { padding: 0 } }}>
              <Box sx={{ height: "calc(100vh - 280px)", width: "100%" }}>
                <DataGrid
                  loading={logsLoading || isFetching}
                  getRowId={(row) => row?.vehicleKey}
                  rows={rows || []}
                  columns={columns}
                  checkboxSelection
                  disableSelectionOnClick
                  experimentalFeatures={{ newEditingApi: true }}
                  page={page}
                  onPageChange={(newPage) => setPage(newPage)}
                  pageSize={pageSize}
                  onPageSizeChange={(newPageSize) => {
                    setPageSize(newPageSize);
                    setPage(0);
                  }}
                  components={{ Toolbar: CustomToolbar }}
                  componentsProps={{
                    toolbar: {
                      totalPages,
                      onPageChange: (newPage) => setPage(newPage),
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};

export default NavigationReport;
